/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { TextInput } from 'carbon-components-react';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  INTELEPEER_SMS_TYPE,
  TWILIO_SMS_TYPE,
  SMS_CHANNEL_TYPE
} from '../../../util/channelTypes';
import { QuickSetupComponentProps } from '../../../util/types';
import { TWILIO_COLOR_PRIMARY } from '../../../util/validation/constants';
import { IntelepeerIcon } from '../../icons/IntelepeerIcon';
import { TwilioIcon } from '../../icons/TwilioIcon';
import { BasicSetup } from '../basic-setup-tab/messages';
import { FIELDS } from '../schema';
import {
  PhoneNumberStepMessages as messages,
  TwilioMessagingQuickSetup
} from './messages';

interface PhoneNumberStepProps extends QuickSetupComponentProps {
  /**
   *
   */
  required?: boolean;
  /**
   *
   */
  providerField?: string;
  /**
   *
   */
  includeLogo?: boolean;
}
export const PhoneNumberStep = ({
  required,
  providerField,
  includeLogo,
  markStepComplete,
  readOnly
}: PhoneNumberStepProps) => {
  const formMethods = useFormContext();
  const { getFieldValue, isValidAndPopulated, isValid, registerInputField } =
    useFormHelpers(formMethods);
  const provider = getFieldValue(providerField);

  const phoneNumberChangeState = useState<boolean>(false);
  const isPhoneNumberValidAndPopulated = isValidAndPopulated(
    FIELDS.FROM_PHONE_NUMBER
  );
  const isPhoneNumberValid = isValid(FIELDS.FROM_PHONE_NUMBER);
  /**
   * When field is valid, mark step complete and enable the
   * next button.
   */
  useEffect(() => {
    markStepComplete(
      required ? isPhoneNumberValidAndPopulated : isPhoneNumberValid
    );
  }, [
    isValidAndPopulated,
    markStepComplete,
    required,
    isPhoneNumberValidAndPopulated,
    isPhoneNumberValid
  ]);

  return (
    <div className="TabContent__body-content TabContent__body-outdent">
      <div className="QuickSetup__step-title">
        <FormattedMessage {...messages.title} />
      </div>
      <div className="TabContent__spacer8" />
      <div className="TabContent__subtitle">
        <FormattedMessage {...messages.description} />
      </div>
      <div className="TabContent__spacer16" />
      {includeLogo && provider === TWILIO_SMS_TYPE && (
        <>
          <TwilioIcon
            className="ServiceDesk--twilio"
            channelType={SMS_CHANNEL_TYPE}
            fill={TWILIO_COLOR_PRIMARY}
          />
          <div className="TabContent__spacer16" />
        </>
      )}
      {includeLogo && provider === INTELEPEER_SMS_TYPE && (
        <>
          <IntelepeerIcon
            className="ServiceDesk--intelepeer"
            channelType={SMS_CHANNEL_TYPE}
          />
          <div className="TabContent__spacer32" />
        </>
      )}
      <div className="TabContent__subtitle">
        <TextInput
          {...registerInputField({
            field: FIELDS.FROM_PHONE_NUMBER,
            isQuickSetup: true,
            isPasswordField: false,
            labelText: TwilioMessagingQuickSetup.phoneNumber,
            placeholderText: BasicSetup.phonePlaceholder,
            changeState: phoneNumberChangeState,
            readOnly
          })}
        />
      </div>
    </div>
  );
};
